<template>
    <BaseField
        :label="label"
        :hint="hint"
        :name="name"
        :data-spec-class="hasIcons ? 'field-switch-icon' : 'field-switch'"
    >
        <div
            v-if="hasIcons || switchLabel"
            class="flex items-center justify-between gap-3"
        >
            <button
                :class="buttonClassObject"
                :aria-checked="modelValue"
                :disabled="isDisabled"
                :data-spec-class="hasIcons ? 'field-switch-icon-button' : 'field-switch-button'"
                @click.prevent="isDisabled ? () => {} : onClick()"
            >
                <span
                    aria-hidden="true"
                    :class="[
                        modelValue ? 'translate-x-5' : 'translate-x-0',
                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out',
                    ]"
                >
                    <!-- Icon when switch is OFF -->
                    <span
                        v-if="iconOff"
                        class="absolute inset-0 flex size-full items-center justify-center transition-opacity duration-200 ease-in-out"
                        :class="[modelValue ? 'opacity-0' : 'opacity-100']"
                        aria-hidden="true"
                    >
                        <component
                            :is="iconOff"
                            class="size-3 text-muted"
                        />
                    </span>
                    <!-- Icon when switch is ON -->
                    <span
                        v-if="iconOn"
                        class="absolute inset-0 flex size-full items-center justify-center transition-opacity duration-200 ease-in-out"
                        :class="[modelValue ? 'opacity-100' : 'opacity-0']"
                        aria-hidden="true"
                    >
                        <component
                            :is="iconOn"
                            class="size-3 text-brand"
                        />
                    </span>
                </span>
            </button>

            <div
                v-if="switchLabel"
                class="text-sm"
            >
                <label
                    :for="name"
                    class="cursor-pointer font-medium"
                    @click="!isDisabled && onClick()"
                >
                    {{ switchLabel }}
                </label>
            </div>
        </div>

        <!-- Fallback for simple switch without icons or label -->
        <button
            v-else
            :class="buttonClassObject"
            :aria-checked="modelValue"
            :disabled="isDisabled"
            data-spec-class="field-switch-button"
            @click.prevent="isDisabled ? () => {} : onClick()"
        >
            <span class="sr-only">Use setting</span>
            <span
                aria-hidden="true"
                :class="[
                    modelValue ? 'translate-x-5' : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out',
                ]"
            />
        </button>
    </BaseField>
</template>

<script setup lang="ts">
import { withDefaults, defineProps, defineEmits, computed } from 'vue';
import type { Component } from 'vue';
import BaseField from './BaseField.vue';

const emits = defineEmits(['update:modelValue', 'click']);

const buttonClass =
    'transition-ease relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-brand';

const buttonClassObject = computed(() => ({
    [buttonClass]: true,
    'bg-muted': !props.modelValue,
    'bg-brand': props.modelValue,
    '!cursor-not-allowed opacity-50': props.isDisabled,
}));

interface Props {
    modelValue?: boolean;
    name: string;
    label?: string;
    hint?: string;
    switchLabel?: string;
    iconOff?: Component; // Vue component for the icon when switch is OFF
    iconOn?: Component; // Vue component for the icon when switch is ON
    isRemoteControlled?: boolean;
    isDisabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: false,
    label: '',
    hint: '',
    switchLabel: '',
    iconOff: undefined,
    iconOn: undefined,
    isRemoteControlled: false,
    isDisabled: false,
});

const hasIcons = computed(() => props.iconOff || props.iconOn);

const onClick = () => {
    if (!props.isDisabled && !props.isRemoteControlled) {
        emits('update:modelValue', !props.modelValue);
    }
    emits('click');
};
</script>
